import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import translations from '../../translations/de';
import Paths from '../../constants/paths';
import Item from '../../components/Item';
import Input from '../../components/Input';
import Select from '../../components/Select';
import parkingAPI from '../../api/parking';

// BernExpo QR pool import. Each CSV row is a ticket: BernExpo's reference from
// the PDF's Vorname field ("Spieltag01_307") and the QR payload printed on it.
// The reference is what ties a code to a match and to a slot, so a payload on
// its own is not enough — the backend refuses a batch it cannot place.
const ImportCsv = ({ store }) => {
    const history = useHistory();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState([]);
    const [fieldId, setFieldId] = useState(null);
    const [batchId, setBatchId] = useState('');
    const [rows, setRows] = useState([]);
    const [parseError, setParseError] = useState(null);
    const [fileName, setFileName] = useState('');

    useEffect(() => {
        !store.dataLoaded && store.setDataLoaded();
        parkingAPI
            .getFields()
            // only fields that use external QR codes can receive a CSV pool
            .then((res) => setFields(res.data.filter((f) => f.uses_external_qr)))
            .catch(() => {});
    }, [store]);

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setParseError(null);
        const reader = new FileReader();
        reader.onload = () => {
            const parsed = [];
            const bad = [];
            String(reader.result)
                .split(/\r?\n/)
                .map((line) => line.trim())
                .filter(Boolean)
                .forEach((line, i) => {
                    const [ref, payload] = line.split(/[;,\t]/).map((c) => (c || '').trim());
                    // Tolerate a header row rather than counting it as an error.
                    if (i === 0 && /vorname|reference|ref/i.test(ref)) return;
                    if (ref && payload) parsed.push({ external_ref: ref, qr_payload: payload });
                    else bad.push(line);
                });

            setRows(parsed);
            // Naming the bad lines beats a silent short import — a row dropped
            // here is a parking card somebody would not get.
            if (bad.length) {
                setParseError(
                    `${bad.length} Zeile(n) ohne Referenz und Code, z.B.: ${bad
                        .slice(0, 3)
                        .join(' | ')}`,
                );
            }
        };
        reader.readAsText(file);
    };

    const submit = () => {
        setError(null);
        if (!fieldId) {
            setError(translations.required + ': ' + translations.parking_field);
            return;
        }
        if (!rows.length) {
            setError(translations.required + ': CSV');
            return;
        }
        setLoading(true);
        store
            .importCsv({
                parking_field_id: fieldId,
                rows,
                import_batch_id: batchId || undefined,
            })
            .then(() => {
                setLoading(false);
                history.push(Paths.PARKING_FIELDS);
            })
            .catch((err) => {
                setError(err);
                setLoading(false);
            });
    };

    return (
        <Item
            links={[{ path: Paths.PARKING_FIELDS, name: translations.parking_fields }]}
            name="Import CSV (QR)"
            itemModel={{}}
            edit={true}
            onSubmit={submit}
            onCancel={() => history.goBack()}
            error={error}
            loading={loading}
        >
            <div style={{ maxWidth: '520px' }}>
                <Select
                    label={translations.parking_field}
                    options={fields.map((f) => ({ label: f.name, value: f.id }))}
                    onChange={(e) => setFieldId(e.value)}
                    noOptionsMsg={translations.uses_external_qr}
                />
                <Input label="Import Batch ID" value={batchId} onChange={(e) => setBatchId(e.target.value)} />
                <div style={{ margin: '15px 0' }}>
                    <p style={{ color: '#666', marginBottom: '8px', fontSize: '13px' }}>
                        CSV mit zwei Spalten: Referenz (Vorname, z.B. Spieltag01_307) und QR-Code.
                    </p>

                    <input type="file" accept=".csv,text/csv" onChange={onFile} />

                    {fileName && (
                        <p style={{ color: '#333', marginTop: '8px' }}>
                            {fileName} — {rows.length} Codes
                        </p>
                    )}

                    {parseError && (
                        <p style={{ color: '#e73c37', marginTop: '4px' }}>{parseError}</p>
                    )}
                </div>
            </div>
        </Item>
    );
};

export default observer(ImportCsv);
